import * as React from 'react'
import {useState} from 'react'
import {observer} from 'mobx-react'
import {Transition} from 'react-transition-group'
import {filter, reduce} from 'lodash'
import {useStores} from '../../../../lib/MobXProvider'
import {DynamicSlotName} from '../../../../lib/DynamicSlotName'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {buildSlideTransitionProps} from '../../../../lib/helpers'
import {DynamicSlot} from '../../../dynamic/DynamicSlot'
import {IconMinus, IconPlus} from '../../../ui/Icons'

///////////////////////////////////////////////////////////////////////////////

export const Faq = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const faqEntriesKeys = api.resolveConfig('faqEntries')
  const [expandedFaqEntriesKeys, setExpandedFaqEntriesKeys] = useState(
    api.resolveConfig('expandedFaqEntries'),
  )
  const {uiTransitionTimeout: timeout} = api.computedConfig
  const slideTransitionProps = buildSlideTransitionProps({timeout})

  const toggleExpanded = (key: string) => {
    if (expandedFaqEntriesKeys.includes(key)) {
      setExpandedFaqEntriesKeys((state) => filter(state, (v) => v !== key))
    } else {
      setExpandedFaqEntriesKeys((state) => [...state, key])
    }
  }

  const faqEntries = reduce(
    faqEntriesKeys,
    (res, v) => {
      const question = api.trans(`faq_entries.${v}.question`, {
        defaultValue: null,
      })
      const answerHTML = api.trans(`faq_entries.${v}.answer_html`, {
        defaultValue: null,
      })
      const expanded = expandedFaqEntriesKeys.includes(v)

      if (question && answerHTML) {
        res.push({key: v, question, answerHTML, expanded})
      }

      return res
    },
    [],
  )

  if (faqEntries.length === 0) {
    return null
  }

  return (
    <section className="faq">
      <div className="header">
        <div className="title">
          <h2 className="text">{api.trans('blocks.faq.subtitle')}</h2>
        </div>
      </div>

      <DynamicSlot name={DynamicSlotName.FAQ_START} />

      <div className="main faq-entries">
        {faqEntries &&
          faqEntries.map((entry, i) => {
            const icon = entry.expanded ? <IconMinus /> : <IconPlus />

            return (
              <div className="faq-entry" key={i}>
                <div className="faq-entry-inner">
                  <button
                    type="button"
                    className="faq-question"
                    onClick={() => toggleExpanded(entry.key)}
                    aria-expanded={entry.expanded}
                    aria-controls={api.ui.generateUniqueElementId(
                      'faq-answer-' + i,
                    )}
                  >
                    <span className="faq-question-inner">
                      <span className="icon" role="presentation">
                        {icon}
                      </span>
                      <span className="title">{entry.question}</span>
                    </span>
                  </button>

                  <div
                    className="faq-answer"
                    id={api.ui.generateUniqueElementId('faq-answer-' + i)}
                  >
                    <Transition
                      in={entry.expanded}
                      mountOnEnter
                      unmountOnExit
                      {...slideTransitionProps}
                    >
                      <div className="faq-answer-inner">
                        <div
                          className="text"
                          dangerouslySetInnerHTML={{__html: entry.answerHTML}}
                        />
                      </div>
                    </Transition>
                  </div>
                </div>
              </div>
            )
          })}
      </div>

      <DynamicSlot name={DynamicSlotName.FAQ_END} />
    </section>
  )
})
